import { useRef, useState } from 'react'
import { ImagePlus, X } from 'lucide-react'
import { AuthCard } from '@/components/portal/auth-card'
import { Wordmark } from '@/components/portal/wordmark'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  useSubmitPublicServiceRequest,
  useUploadPublicRequestAttachment,
} from '@/hooks/use-requests'
import { getApiErrorMessage } from '@/lib/api'
import type { ServiceRequestType } from '@riverpark/types'

const SELECT_CLS =
  'flex h-10 w-full rounded border border-input bg-card px-3.5 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20'

const TYPES: { value: ServiceRequestType; label: string }[] = [
  { value: 'maintenance', label: 'Maintenance' },
  { value: 'complaint', label: 'Complaint' },
  { value: 'general', label: 'General' },
]

/** Public, no-login page for the QR-code / print-poster flow — anyone who scans the code can
 * report an issue without a portal account. Plot number is optional and best-effort matched
 * server-side; it never blocks submission if it doesn't resolve to a real plot. */
export function PublicReportPage() {
  const submit = useSubmitPublicServiceRequest()
  const uploadAttachment = useUploadPublicRequestAttachment()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [type, setType] = useState<ServiceRequestType>('maintenance')
  const [plotNumber, setPlotNumber] = useState('')
  const [subject, setSubject] = useState('')
  const [body, setBody] = useState('')
  const [reporterName, setReporterName] = useState('')
  const [reporterContact, setReporterContact] = useState('')
  const [photos, setPhotos] = useState<File[]>([])
  const [error, setError] = useState<string | null>(null)
  const [submittedId, setSubmittedId] = useState<string | null>(null)
  const [saveStep, setSaveStep] = useState<string | null>(null)

  function handlePhotosChange(e: React.ChangeEvent<HTMLInputElement>) {
    setPhotos((prev) => [...prev, ...Array.from(e.target.files ?? [])])
    e.target.value = ''
  }

  function removePhoto(index: number) {
    setPhotos((prev) => prev.filter((_, i) => i !== index))
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setError(null)
    try {
      setSaveStep('Submitting report…')
      const result = await submit.mutateAsync({
        type,
        subject,
        body,
        ...(plotNumber && { plotNumber }),
        ...(reporterName && { reporterName }),
        ...(reporterContact && { reporterContact }),
      })
      for (const [index, photo] of photos.entries()) {
        setSaveStep(`Uploading photo ${index + 1} of ${photos.length}…`)
        await uploadAttachment.mutateAsync({ requestId: result.data.id, file: photo })
      }
      setSubmittedId(result.data.id)
    } catch (err) {
      setError(getApiErrorMessage(err, 'Failed to submit your report'))
    } finally {
      setSaveStep(null)
    }
  }

  const pending = submit.isPending || uploadAttachment.isPending

  if (submittedId) {
    return (
      <AuthCard className="max-w-[440px] text-center">
        <Wordmark portal="Estate Portal" className="mb-8" />
        <h2 className="mb-2 font-serif text-2xl font-medium text-ink">Report received</h2>
        <p className="mb-1 font-sans text-sm text-muted-foreground">
          Thank you — the estate team has been notified.
        </p>
        <p className="font-sans text-xs text-muted-foreground">
          Reference: <span className="font-medium text-ink">{submittedId.slice(0, 8)}</span>
        </p>
      </AuthCard>
    )
  }

  return (
    <AuthCard className="max-w-[440px]">
      <Wordmark portal="Estate Portal" className="mb-8" />
      <h2 className="mb-1 text-center font-serif text-2xl font-medium text-ink">Report an issue</h2>
      <p className="mb-6 text-center font-sans text-sm text-muted-foreground">
        No account needed — tell us what's wrong and where.
      </p>

      <form onSubmit={handleSubmit} className="space-y-4">
        <div className="space-y-1.5">
          <label className="font-sans text-sm font-medium text-ink">Type</label>
          <select
            className={SELECT_CLS}
            value={type}
            onChange={(e) => setType(e.target.value as ServiceRequestType)}
          >
            {TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
        </div>
        <Input
          label="Plot number (optional)"
          value={plotNumber}
          onChange={(e) => setPlotNumber(e.target.value)}
          hint="If you know it, this links your report to the right plot."
          maxLength={20}
        />
        <Input
          label="Subject"
          value={subject}
          onChange={(e) => setSubject(e.target.value)}
          required
          minLength={5}
          maxLength={200}
        />
        <div className="space-y-1.5">
          <label htmlFor="report-body" className="font-sans text-sm font-medium text-ink">
            What's the issue?
          </label>
          <textarea
            id="report-body"
            value={body}
            onChange={(e) => setBody(e.target.value)}
            rows={5}
            required
            minLength={20}
            maxLength={2000}
            className="flex w-full rounded border border-input bg-card px-3.5 py-2 font-sans text-sm text-ink focus-visible:border-primary focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-green/20"
          />
        </div>
        <Input
          label="Your name (optional)"
          value={reporterName}
          onChange={(e) => setReporterName(e.target.value)}
          maxLength={100}
        />
        <Input
          label="Phone or email (optional)"
          value={reporterContact}
          onChange={(e) => setReporterContact(e.target.value)}
          hint="Only if you'd like the estate team to follow up with you."
          maxLength={100}
        />

        <div className="space-y-1.5">
          <label className="font-sans text-sm font-medium text-ink">Photos (optional)</label>
          {photos.length > 0 && (
            <ul className="space-y-1">
              {photos.map((photo, index) => (
                <li
                  key={`${photo.name}-${index}`}
                  className="flex items-center justify-between gap-2 rounded border border-border-2 bg-cream-2 px-2.5 py-1.5 font-sans text-xs text-ink"
                >
                  <span className="truncate">{photo.name}</span>
                  <button
                    type="button"
                    onClick={() => removePhoto(index)}
                    className="shrink-0 text-muted-foreground hover:text-destructive"
                    aria-label={`Remove ${photo.name}`}
                  >
                    <X size={14} />
                  </button>
                </li>
              ))}
            </ul>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/jpeg,image/png,image/heic,image/heif"
            multiple
            className="hidden"
            onChange={handlePhotosChange}
          />
          <button
            type="button"
            onClick={() => fileInputRef.current?.click()}
            className="flex items-center gap-1.5 rounded border border-dashed border-border-2 px-3 py-2 font-sans text-xs font-medium text-muted-foreground transition-colors hover:border-primary hover:text-ink"
          >
            <ImagePlus size={14} />
            Add photo
          </button>
        </div>

        {error && (
          <p className="font-sans text-xs text-destructive" role="alert">
            {error}
          </p>
        )}
        <Button type="submit" loading={pending} className="w-full">
          {saveStep ?? 'Submit report'}
        </Button>
      </form>
    </AuthCard>
  )
}
